<script setup>
// The mobile navigation, as the one theatrical control on the marketing surface:
// a square hamburger that morphs to an X while a bold ink-bordered panel drops in
// and the links cascade. Adapted from the classic GSAP "island menu" into the civic
// language — square corners, 2px ink border, hard shadow, cobalt on hover, no pill,
// no glass. Each tween carries its own `easeReverse`, so it opens with an overshoot
// (back.out) and closes on a smooth curve (power) rather than simply rewinding.
//
// Desktop keeps the inline nav in LandingNav; this is md:hidden and fills the real
// gap that a signed-out visitor on a phone otherwise has (no way to reach the
// sections). Teleported to <body> so it overlays everything and never precedes the
// header's own links in the DOM. Reduced-motion users get an instant, un-animated
// open/close with the same focus behaviour.
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { X, ArrowRight } from "@lucide/vue";
import BrandMark from "@/components/BrandMark.vue";
import { buttonVariants } from "@/components/ui/button";
import { scrollToId } from "@/lib/scroll";
import { gsap, reducedMotion } from "@/lib/motion";

const { t } = useI18n();

// Passed straight down from LandingNav, so the phone menu and the desktop bar can
// never disagree about which page they're on. See the prop comment there.
const props = defineProps({
  anchors: { type: Array, default: null },
  awayLink: { type: Object, default: null },
});

// Document order, matching LandingNav's desktop list.
const HOME_ANCHORS = [
  { href: "#hoe-het-werkt", key: "landing.nav.how" },
  { href: "#geldstroom", key: "landing.nav.flows" },
  { href: "#prijzen", key: "landing.nav.pricing" },
  { href: "#vragen", key: "landing.nav.faq" },
];

const items = computed(() => props.anchors ?? HOME_ANCHORS.map(({ href, key }) => ({ href, label: t(key) })));
const away = computed(() => props.awayLink ?? { to: "/voor-vve-beheerders", label: t("landing.nav.managers") });

const isOpen = ref(false);

const btn = ref(null);
const overlay = ref(null);
const backdrop = ref(null);
const panel = ref(null);
const barTop = ref(null);
const barMid = ref(null);
const barBot = ref(null);

let tl = null;
let prevOverflow = "";
let prevHtmlOverflow = "";

function focusables() {
  if (!panel.value) return [];
  return [...panel.value.querySelectorAll("a[href], button:not([disabled])")].filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

onMounted(() => {
  // Closed resting state for everything the timeline drives.
  gsap.set(backdrop.value, { opacity: 0 });
  gsap.set(panel.value, { autoAlpha: 0, yPercent: -8, scale: 0.94, transformOrigin: "top center" });
  gsap.set(panel.value.querySelectorAll("[data-menu-link]"), { autoAlpha: 0, y: 12 });

  if (reducedMotion) return;

  tl = gsap
    .timeline({
      paused: true,
      onReverseComplete: () => gsap.set(overlay.value, { autoAlpha: 0, pointerEvents: "none" }),
    })
    .to(backdrop.value, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0)
    // Bars fold into an X; the middle simply dissolves.
    .to(barMid.value, { opacity: 0, duration: 0.14, ease: "power2.in", easeReverse: "power2.out" }, 0)
    .to(barTop.value, { y: 5, rotate: 45, duration: 0.3, ease: "power3.inOut" }, 0)
    .to(barBot.value, { y: -5, rotate: -45, duration: 0.3, ease: "power3.inOut" }, 0)
    .to(
      panel.value,
      { autoAlpha: 1, yPercent: 0, scale: 1, duration: 0.5, ease: "back.out(2)", easeReverse: "power3.out" },
      0.05,
    )
    .to(
      panel.value.querySelectorAll("[data-menu-link]"),
      { autoAlpha: 1, y: 0, duration: 0.34, ease: "power2.out", easeReverse: "power2.in", stagger: 0.05 },
      0.16,
    );
});

onUnmounted(() => {
  tl?.kill();
  document.body.style.overflow = prevOverflow;
  document.documentElement.style.overflow = prevHtmlOverflow;
});

async function open() {
  isOpen.value = true;
  prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  // The actual scroll-snap container is <html> (html.snap-pages), not <body> — iOS
  // Safari in particular doesn't fully honour overflow:hidden on body alone, so
  // without this a background touch-drag could still shift/snap the page underneath
  // the "locked" menu.
  prevHtmlOverflow = document.documentElement.style.overflow;
  document.documentElement.style.overflow = "hidden";
  gsap.set(overlay.value, { autoAlpha: 1, pointerEvents: "auto" });

  if (tl) tl.play();
  else {
    gsap.set(backdrop.value, { opacity: 1 });
    gsap.set(panel.value, { autoAlpha: 1, yPercent: 0, scale: 1 });
    gsap.set(panel.value.querySelectorAll("[data-menu-link]"), { autoAlpha: 1, y: 0 });
  }

  await nextTick();
  focusables()[0]?.focus();
}

function close({ restoreFocus = true } = {}) {
  isOpen.value = false;
  document.body.style.overflow = prevOverflow;
  document.documentElement.style.overflow = prevHtmlOverflow;

  if (tl) tl.reverse();
  else gsap.set(overlay.value, { autoAlpha: 0, pointerEvents: "none" });

  if (restoreFocus) btn.value?.focus();
}

function onLinkClick(event) {
  const href = event.currentTarget.getAttribute("href");
  event.preventDefault();
  isOpen.value = false;
  document.body.style.overflow = prevOverflow;
  document.documentElement.style.overflow = prevHtmlOverflow;
  // Jump straight to closed instead of close()'s animated reverse: on a phone,
  // reversing the panel/backdrop while the page simultaneously scroll-snaps to the
  // target section was two motions overlapping in the same small viewport, which
  // read as jittery. Overflow is already restored above, so the scroll below is
  // free to run immediately — no reason to wait on the animation either.
  tl?.pause(0);
  gsap.set(overlay.value, { autoAlpha: 0, pointerEvents: "none" });
  if (href?.startsWith("#")) {
    scrollToId(href.slice(1));
    history.replaceState(history.state, "", href);
  }
}

function onKeydown(event) {
  if (event.key === "Escape") {
    close();
    return;
  }
  if (event.key !== "Tab") return;
  const items = focusables();
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
</script>

<template>
  <button
    ref="btn"
    type="button"
    :class="buttonVariants({ variant: 'outline', size: 'icon' })"
    :aria-expanded="isOpen"
    aria-controls="landing-menu"
    :aria-label="isOpen ? t('landing.nav.close_menu') : t('landing.nav.menu')"
    @click="isOpen ? close() : open()"
  >
    <span class="relative block h-3.5 w-5" aria-hidden="true">
      <span ref="barTop" class="absolute inset-x-0 top-0 h-0.5 bg-current" />
      <span ref="barMid" class="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-current" />
      <span ref="barBot" class="absolute inset-x-0 bottom-0 h-0.5 bg-current" />
    </span>
  </button>

  <Teleport to="body">
    <div
      ref="overlay"
      id="landing-menu"
      class="invisible fixed inset-0 z-50"
      style="pointer-events: none"
      role="dialog"
      aria-modal="true"
      :aria-label="t('landing.nav.menu')"
      @keydown="onKeydown"
    >
      <div ref="backdrop" class="absolute inset-0 touch-none bg-foreground/85" @click="close()" />

      <div
        ref="panel"
        class="absolute inset-x-3 top-3 max-h-[calc(100dvh-1.5rem)] overflow-y-auto border-2 border-foreground bg-card p-2 shadow-[var(--shadow-hard-lg)]"
      >
        <div class="flex items-center justify-between px-2 py-1.5">
          <span class="inline-flex items-center gap-2">
            <BrandMark class="size-5 text-primary" />
            <span class="font-sans text-lg font-bold tracking-tight text-foreground">Pandig</span>
          </span>
          <button
            type="button"
            :class="buttonVariants({ variant: 'ghost', size: 'icon' })"
            :aria-label="t('landing.nav.close_menu')"
            @click="close()"
          >
            <X class="size-5" />
          </button>
        </div>

        <nav class="mt-1 grid" aria-label="Op deze pagina">
          <a
            v-for="(item, i) in items"
            :key="item.href"
            :href="item.href"
            data-menu-link
            class="flex items-center justify-between border-t-2 border-foreground/10 px-2 py-3.5 font-sans text-lg font-semibold text-foreground transition-colors hover:text-primary"
            @click="onLinkClick"
          >
            {{ item.label }}
            <span class="font-mono text-xs font-semibold text-muted-foreground">{{ String(i + 1).padStart(2, "0") }}</span>
          </a>
        </nav>

        <div class="mt-2 grid gap-2 border-t-2 border-foreground/10 pt-3">
          <!-- The one item that leaves the page — the manager page from home, and the
               way back from there. On a phone this menu is the only header route off
               the current page, so it is never hidden. -->
          <RouterLink
            :to="away.to"
            data-menu-link
            class="flex items-center justify-between px-2 pb-3 font-sans text-base font-semibold text-foreground transition-colors hover:text-primary"
            @click="close({ restoreFocus: false })"
          >
            {{ away.label }}
            <ArrowRight class="size-4 text-muted-foreground" />
          </RouterLink>
          <RouterLink to="/signup" data-menu-link :class="buttonVariants({ size: 'lg' })" @click="close({ restoreFocus: false })">
            {{ t("landing.nav.sign_up") }}
          </RouterLink>
          <RouterLink
            to="/login"
            data-menu-link
            :class="buttonVariants({ variant: 'outline', size: 'lg' })"
            @click="close({ restoreFocus: false })"
          >
            {{ t("landing.nav.sign_in") }}
          </RouterLink>
        </div>
      </div>
    </div>
  </Teleport>
</template>
